import React, { useContext } from 'react'
import { NarrativeBlock as NarrativeBlockData, PacketStrategy } from '@marketing-milk/interfaces'
import { SurfacePalette, type } from '../../../tokens'
import { useSprint } from 'hooks/api/useSprint'
import { useSectionNumber } from '../../PacketSectionsContext'
import { PresentPage } from '../PresentPage'
import { CreativeCardList } from '../../sections/CreativeCardList'
import { useCreativeApproval } from '../../sections/useCreativeApproval'
import { useCreativePreview } from '../../sections/useCreativePreview'
import { PresentNavLockContext } from '../PresentNavLock'

/**
 * Which of the Creative section's slides this is. The section spans several
 * slides — an intro, one per strategic theme, then anything not yet grouped —
 * but they ALL carry section number 09: numbering them 09/10/11/12 would make
 * an eleven-section packet look like it has fifteen.
 */
export type CreativeSlide =
  | { kind: 'intro' }
  | { kind: 'group'; groupID: number; label: string }
  | { kind: 'ungrouped' }

interface Props {
  palette: SurfacePalette
  businessID: number
  packetID: number
  sprintID?: number
  slide: CreativeSlide
  strategy?: PacketStrategy
  narrative: NarrativeBlockData[]
}

/**
 * Present-mode (dark) Creative slide. The slide chrome carries the packet-local
 * `section` anchor; each creative card inside carries its own `creative` anchor.
 * In-meeting approval is enabled here too (the client is present), reusing the
 * shared approval flow; while a modal is open we lock deck navigation so Arrow/Esc
 * don't move slides or exit the presentation mid-edit.
 *
 * Moving and editing are Edit-only — re-sorting in front of the client would
 * reshuffle the deck mid-meeting — so no `editing` prop is passed down.
 */
export const PresentCreativeSection = ({
  palette,
  businessID,
  packetID,
  sprintID,
  slide,
  strategy,
  narrative,
}: Props) => {
  const { sprintCreatives, sprintCreativesLoading, refetchCreatives } = useSprint(sprintID)
  const setNavLocked = useContext(PresentNavLockContext)
  const { onApprove, onRemoveApproval, modals } = useCreativeApproval(
    businessID,
    palette,
    setNavLocked
  )
  // In-meeting approval is enabled here too, surfaced from the preview action bar
  // (the client sees the full ad before the AE approves). No editor link in
  // Present — it stays read-only for source edits.
  const {
    onPreview,
    prefetch,
    modals: previewModals,
  } = useCreativePreview(
    palette,
    setNavLocked,
    {
      onApprove,
      onRemoveApproval,
    },
    { creatives: sprintCreatives, refetchCreatives }
  )

  const number = useSectionNumber('creative')

  const title =
    slide.kind === 'group' ? slide.label : slide.kind === 'ungrouped' ? 'More creative' : 'Creative'

  const introText =
    narrative.find(b => b.sectionKey === 'creative-strategy')?.richText?.trim() ?? ''

  return (
    <PresentPage
      palette={palette}
      number={number}
      title={title}
      commentAnchor={{ type: 'section', id: `${packetID}:creative`, label: 'Creative' }}
    >
      {slide.kind === 'intro' ? (
        <div
          style={{
            fontFamily: type.family.serif,
            fontSize: 32,
            lineHeight: 1.4,
            whiteSpace: 'pre-wrap',
            color: introText ? palette.text : palette.textFaint,
            fontStyle: introText ? 'normal' : 'italic',
            maxWidth: 760,
          }}
        >
          {introText || 'Nothing captured for this section.'}
        </div>
      ) : (
        <CreativeCardList
          palette={palette}
          creatives={sprintCreatives}
          strategy={strategy}
          loading={sprintCreativesLoading}
          noSprint={!sprintID}
          onPreview={onPreview}
          onPrefetch={prefetch}
          onlyGroupID={slide.kind === 'group' ? slide.groupID : 'ungrouped'}
        />
      )}
      {modals}
      {previewModals}
    </PresentPage>
  )
}
